import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  IconButton,
  MenuItem,
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import Iconify from '../components/iconify';

import { useNavigate } from 'react-router-dom';
import { api } from 'src/api/api-client.config';
import { LeagueTournamentDialog } from 'src/components/pickem-admin/LeagueTournamentDialog';
import { useAppSearchParams } from '@/hooks/useAppSearchParams.ts';
import { formatDateWithTimezone } from 'src/components/Timezone/helpers';
import { useTimezoneStore } from 'src/store/timezoneStore';

export default function LeagueTournamentsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<any[]>([]);
  const [openTournamentDialog, setOpenTournamentDialog] = useState(false);
  const [editTournament, setEditTournament] = useState(null);
  const [openPopover, setOpenPopover] = useState(null);
  const [openId, setOpenId] = useState<number>(-1);

  const { leagueId } = useAppSearchParams();
  const { timezone } = useTimezoneStore();

  useEffect(() => {
    if (!leagueId) return;

    api
      .get<void, { tournaments: any[] }>(
        `/admin/leagues/${leagueId}/tournaments`
      )
      .then((data) => {
        setRows(data.tournaments);
      });
  }, []);

  const successTournamentHandler = (tournament: any) => {
    if (tournament?.league_tournament_id) {
      api
        .put(
          `/admin/leagues/${leagueId}/tournaments/${tournament.league_tournament_id}`,
          tournament
        )
        .then(() => {
          window.location.reload();
        });
    } else {
      api
        .post(`/admin/leagues/${leagueId}/tournaments`, tournament)
        .then(() => {
          window.location.reload();
        });
    }
  };

  const editTournamentHandler = (tournamentId: number) => {
    const editTournament = rows.find(
      (row) => row.league_tournament_id === tournamentId
    );

    setEditTournament(editTournament);
    setOpenTournamentDialog(true);
  };

  const closeTournamentDialog = () => {
    setEditTournament(null);
    setOpenTournamentDialog(false);
  };

  const openAddTournamentDialog = () => {
    setEditTournament(null);
    setOpenTournamentDialog(true);
  };

  const handleOpenMenu = (event: any) => {
    setOpenPopover(event.currentTarget);
    setOpenId(Number(event.currentTarget.id));
  };

  const handleCloseMenu = () => {
    setOpenPopover(null);
    setOpenId(-1);
  };

  return (
    <>
      <Helmet>
        <title>League Tournaments</title>
      </Helmet>

      <Container>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          mb={5}
        >
          <Typography variant='h4' gutterBottom>
            League Tournaments
          </Typography>
          <Box>
            <Button
              variant='outlined'
              startIcon={<Iconify icon='eva:close-outline' />}
              onClick={() => navigate(`/dashboard/leagues`)}
              sx={{ mr: 2 }}
            >
              Back
            </Button>
            <Button
              variant='contained'
              startIcon={<Iconify icon='eva:plus-fill' />}
              onClick={() => openAddTournamentDialog()}
            >
              Create Tournament
            </Button>
          </Box>
        </Stack>

        <Card>
          {rows === null ? (
            <Box
              sx={{ display: 'flex', justifyContent: 'center' }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label='simple table'>
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Visible</TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Start ({timezone})</TableCell>
                      <TableCell>End ({timezone})</TableCell>
                      <TableCell>Prize Image</TableCell>
                      <TableCell>Prize Image Fr</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                        }}
                      >
                        <TableCell component='th' scope='row'>
                          {row.league_tournament_id}
                        </TableCell>
                        <TableCell component='th' scope='row'>
                          {row.is_visible ? (
                            <Iconify
                              icon={'eva:checkmark-circle-2-outline'}
                              sx={{ color: 'green' }}
                            />
                          ) : (
                            <Iconify
                              icon={'eva:close-circle-outline'}
                              sx={{ color: 'red' }}
                            />
                          )}
                        </TableCell>
                        <TableCell component='th' scope='row'>
                          {row.title}
                        </TableCell>
                        <TableCell component='th' scope='row'>
                          {row.tournament_type}
                        </TableCell>
                        <TableCell>
                          {formatDateWithTimezone(row.start_time, timezone)}
                        </TableCell>
                        <TableCell>
                          {formatDateWithTimezone(row.end_time, timezone)}
                        </TableCell>
                        <TableCell>
                          <img style={{ width: 100 }} src={row.prize_image} />
                        </TableCell>
                        <TableCell>
                          <img
                            style={{ width: 100 }}
                            src={row.prize_image_fr}
                          />
                        </TableCell>
                        <TableCell component='th' scope='row'>
                          <IconButton
                            size='large'
                            color='inherit'
                            onClick={handleOpenMenu}
                            id={row.league_tournament_id}
                          >
                            <Iconify icon={'eva:more-vertical-fill'} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Card>
      </Container>

      <Popover
        open={Boolean(openPopover)}
        anchorEl={openPopover}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        <MenuItem
          onClick={() =>
            navigate(
              `/dashboard/leaderboards/view?league_id=${leagueId}&tournament_id=${openId}`
            )
          }
        >
          <Iconify icon={'eva:list-outline'} sx={{ mr: 1 }} />
          Leaderboard
        </MenuItem>

        <MenuItem onClick={() => editTournamentHandler(openId)}>
          <Iconify icon={'eva:edit-outline'} sx={{ mr: 1 }} />
          Edit
        </MenuItem>
      </Popover>
      <LeagueTournamentDialog
        tournament={editTournament}
        open={openTournamentDialog}
        onSuccessHandler={successTournamentHandler}
        onClose={closeTournamentDialog}
      />
    </>
  );
}
